/**
 * Game-facing dungeon level: generation, visuals, colliders, spawn helpers.
 * Replaces the open-square Arena. Generation only runs from Loading via build().
 */

import * as THREE from 'three';
import {
  generateDungeon,
  resolveThemeKey,
  makeRng,
  FLOOR,
  WALL,
  POOL,
  TYPE
} from './generateDungeon.js';
import { DungeonBuilder } from './DungeonBuilder.js';
import { createWall } from '../../entities/Wall.js';
import { createPickup } from '../../entities/PickupEntity.js';
import { DROPS, rollWeaponPowerup } from '../../config/Powerups.js';
import { createProp } from '../../entities/Prop.js';
import { createEnemy } from '../../entities/Enemy.js';
import {
  Transform,
  Collider,
  Health,
  Explosive,
  ExplosiveTrigger,
  Destructible,
  Lifetime,
  Chest,
  InteractionTarget
} from '../../components/index.js';
import { Layer } from '../../config/Layers.js';
import { DUNGEON_PROPS } from '../../config/DungeonProps.js';
import { levelLab, labAssetTuning } from '../../config/LevelLab.js';
import { Events } from '../../core/EventBus.js';
import { NavGrid } from '../../ai/NavGrid.js';
import { NAV_CELL_SIZE } from '../../ai/sharedNav.js';

/**
 * Applies the per-floor progression curve to a base knob.
 * @param {number} base Floor-1 value.
 * @param {{ perLevel: number, max: number }} spec Curve entry.
 * @param {number} step `levelIndex - 1` (0 on floor 1).
 * @param {boolean} enabled Whether progression is active.
 * @returns {number}
 */
function curveValue(base, spec, step, enabled) {
  if (!enabled || !spec || step <= 0) return base;
  return Math.min(spec.max, base + spec.perLevel * step);
}

/**
 * @param {number} runSeed
 * @param {number} levelIndex 1-based
 */
export function levelSeed(runSeed, levelIndex) {
  return (Math.imul(runSeed >>> 0, 0x9e3779b1) ^ Math.imul(levelIndex, 0x85ebca6b)) >>> 0;
}

export class DungeonArena {
  /**
   * @param {import('../../core/Context.js').GameContext} ctx
   */
  constructor(ctx) {
    /** @type {import('../../core/Context.js').GameContext} @private */
    this._ctx = ctx;
    /** @type {THREE.Group | null} */
    this.group = null;
    /** @type {import('../../ecs/Entity.js').Entity[]} */
    this.entities = [];
    /** @type {{ minX: number, maxX: number, minZ: number, maxZ: number }} */
    this.bounds = { minX: 0, maxX: 0, minZ: 0, maxZ: 0 };
    /** @type {{ x: number, z: number }} */
    this.entrance = { x: 0, z: 0 };
    /** @type {{ x: number, z: number }} */
    this.exit = { x: 0, z: 0 };
    /** @type {number} */
    this.exitRadius = 2.5;
    /** @type {object | null} */
    this.dungeon = null;
    /** @type {string | null} */
    this.themeKey = null;
    /** @type {string | null} */
    this.name = null;
    /** @type {number} */
    this.seed = 0;
    /** @type {DungeonBuilder | null} @private */
    this._builder = null;
    /**
     * Solid AABBs used by collision and navigation.
     * @type {Array<{x:number,z:number,halfX:number,halfZ:number}>}
     */
    this.blockers = [];
    /**
     * Lazily-built navigation grid, shared by every AI system through
     * {@link ../../ai/sharedNav.js navGridForArena}. Rebuilt only when the
     * level is (dungeon changes replace the whole arena), never during PLAYING.
     * @type {NavGrid | null}
     * @private
     */
    this._navGrid = null;
    /** @type {Array<{x:number,z:number,roomId:number}>} @private */
    this._floorSpawns = [];
    /** @type {number} @private */
    this._tileSize = 2;
    /** @type {number} @private */
    this._W = 0;
    /** @type {number} @private */
    this._H = 0;
    /** @type {(() => void) | null} Unsubscribe for the DIED listener. @private */
    this._diedUnsub = null;
    /** @type {(() => void) | null} Unsubscribe for the CHEST_OPENED listener. @private */
    this._chestOpenedUnsub = null;
    /** @type {number} Enemies pre-placed by the generator. @private */
    this._prePlacedCount = 0;
  }

  /**
   * Generates and builds the dungeon. Must complete before PLAYING.
   * @returns {boolean} false if generation failed every attempt
   */
  build() {
    const { config, scene, lighting } = this._ctx;
    const dcfg = config.dungeon;
    this._tileSize = dcfg.tileSize;

    // The level lab (debug window → Levels) may pin the seed and the theme so
    // a generation can be reproduced and inspected; both fall back to the run.
    const seed =
      levelLab.seed != null
        ? levelLab.seed >>> 0
        : this._ctx.levelSeed != null
          ? this._ctx.levelSeed >>> 0
          : levelSeed(this._ctx.runSeed ?? (Math.random() * 0xffffffff) >>> 0, this._ctx.levelIndex ?? 1);

    // A rebuild replaces the geometry, so any grid rasterised from the old
    // blockers is stale; drop it and let the next query rebuild lazily.
    this._navGrid = null;

    const themePref = levelLab.theme !== 'auto' ? levelLab.theme : dcfg.theme;
    const themeKey = resolveThemeKey(seed, themePref, this._ctx.levelIndex ?? 1);

    // Per-floor progression: floor 1 (or progression disabled) === the base knobs.
    const prog = dcfg.progression;
    const progOn = Boolean(prog?.enabled);
    const step = Math.max(0, (this._ctx.levelIndex ?? 1) - 1);
    const roomCount = Math.round(curveValue(dcfg.roomCount, prog?.roomCount, step, progOn));
    const loopChance = curveValue(dcfg.loopChance, prog?.loopChance, step, progOn);
    const decorDensity = curveValue(dcfg.decorDensity, prog?.decorDensity, step, progOn) * levelLab.densityMult;
    const enemyScale = progOn && prog?.enemies ? Math.min(prog.enemies.max, 1 + prog.enemies.perLevel * step) : 1;

    // Pre-population only happens in modes that ask for it; 'waves' keeps the
    // legacy empty-dungeon-then-waves behaviour.
    const pop = dcfg.population ?? {};
    const prePlace = pop.enabled !== false && (pop.mode === 'prepopulated' || pop.mode === 'both');

    const dungeon = generateDungeon({
      seed,
      roomCount,
      loopChance,
      decorDensity,
      themeKey,
      corridors: dcfg.corridors,
      assetTuning: labAssetTuning(),
      population: {
        ...pop,
        enabled: prePlace,
        tileSize: this._tileSize,
        safeRadiusTiles: (pop.safeRadiusFromEntrance ?? 0) / this._tileSize,
        exitRadiusTiles: 2,
        levelScale: enemyScale
      }
    });

    if (!dungeon.valid) {
      console.error('[DungeonArena] generation failed', dungeon.stats);
      return false;
    }

    this.dungeon = dungeon;
    this.seed = dungeon.seed;
    this.themeKey = themeKey;
    this.name = dungeon.name;
    this._W = dungeon.W;
    this._H = dungeon.H;

    const gl = this._ctx.renderer?.instance;
    const maxAniso = gl?.capabilities?.getMaxAnisotropy?.() ?? 4;

    this._builder = new DungeonBuilder({
      scene,
      tileSize: this._tileSize,
      maxAnisotropy: Math.min(4, maxAniso),
      maxTorchLights: dcfg.maxTorchLights ?? 24,
      wallCutout: config.wallCutout,
      onTheme: (TH) => {
        // Theme-driven lighting/fog on the shared scene
        if (scene.fog) scene.fog.color.set(TH.fog);
        if (scene.background?.isColor) scene.background.set(TH.bg);
        lighting.hemisphere.color.set(TH.hemi[0]);
        lighting.hemisphere.groundColor.set(TH.hemi[1]);
        lighting.hemisphere.intensity = TH.hemi[2];
        lighting.key.color.set(TH.dir[0]);
        lighting.key.intensity = Math.max(0.6, TH.dir[1] * 1.35);
        gl?.setClearColor(TH.bg, 1);
      }
    });

    this.group = this._builder.build(dungeon);
    this._computeWorldAnchors(dungeon);
    this._buildColliders(dungeon);
    this._collectFloorSpawns(dungeon);
    this._buildDestructibles(dungeon);
    this._buildChestInteractions(dungeon);
    this._buildShrineWeapons(dungeon);
    this._populateEnemies(dungeon);

    // Destructible props remove themselves from the arena when they die.
    this._diedUnsub?.();
    this._diedUnsub = this._ctx.events.on(Events.DIED, ({ entity }) =>
      this._onEntityDied(entity)
    );

    // An opened chest hides its own baked instance (Bug: chests must disappear
    // once opened, not just stop offering the `E` prompt).
    this._chestOpenedUnsub?.();
    this._chestOpenedUnsub = this._ctx.events.on(Events.CHEST_OPENED, ({ index }) =>
      this._builder?.hideChestAt(index)
    );

    return true;
  }

  /** @returns {number} Enemies the generator pre-placed at build time. */
  get prePlacedCount() {
    return this._prePlacedCount;
  }

  /**
   * The wall material's see-through holes, for whoever is tracking which
   * bodies need one. Rebuilt with the level, so callers must re-read it rather
   * than hold on to one.
   * @returns {import('../../rendering/WallCutout.js').WallCutout | null}
   */
  get wallCutout() {
    return this._builder?.mats?.wallCutout ?? null;
  }

  /**
   * Tile (tx,ty) → world XZ (group is centered and scaled).
   * @param {number} tx
   * @param {number} ty
   * @returns {{ x: number, z: number }}
   */
  tileToWorld(tx, ty) {
    const ts = this._tileSize;
    const x = (tx - this._W / 2 + 0.5) * ts;
    const z = (ty - this._H / 2 + 0.5) * ts;
    return { x, z };
  }

  /** @param {object} d @private */
  _computeWorldAnchors(d) {
    const er = d.rooms[d.entrance];
    const br = d.rooms[d.boss];
    this.entrance = this.tileToWorld(er.cx, er.cy);
    this.exit = this.tileToWorld(br.cx, br.cy);
    this.exitRadius = Math.max(2.2, Math.min(er.w, er.h, br.w, br.h) * 0.2 * this._tileSize);

    // Bounds from floor extents
    let minX = Infinity,
      maxX = -Infinity,
      minZ = Infinity,
      maxZ = -Infinity;
    for (let y = 0; y < d.H; y++) {
      for (let x = 0; x < d.W; x++) {
        const c = y * d.W + x;
        if (d.grid[c] !== FLOOR && d.grid[c] !== POOL) continue;
        const w = this.tileToWorld(x, y);
        minX = Math.min(minX, w.x);
        maxX = Math.max(maxX, w.x);
        minZ = Math.min(minZ, w.z);
        maxZ = Math.max(maxZ, w.z);
      }
    }
    const pad = this._tileSize;
    this.bounds = {
      minX: minX - pad,
      maxX: maxX + pad,
      minZ: minZ - pad,
      maxZ: maxZ + pad
    };
  }

  /**
   * Merge wall/pool cells into fewer AABBs for collision.
   * @param {object} d
   * @private
   */
  _buildColliders(d) {
    const ts = this._tileSize;
    const solid = new Uint8Array(d.W * d.H);
    for (let i = 0; i < solid.length; i++) {
      if (d.grid[i] === WALL || d.grid[i] === POOL) solid[i] = 1;
    }

    // Greedy rectangle merge of solid cells for fewer static colliders.
    const visited = new Uint8Array(solid.length);
    for (let y = 0; y < d.H; y++) {
      for (let x = 0; x < d.W; x++) {
        const i = y * d.W + x;
        if (!solid[i] || visited[i]) continue;

        let x1 = x;
        while (x1 + 1 < d.W && solid[y * d.W + (x1 + 1)] && !visited[y * d.W + (x1 + 1)]) x1++;

        let y1 = y;
        outer: while (y1 + 1 < d.H) {
          for (let xx = x; xx <= x1; xx++) {
            const j = (y1 + 1) * d.W + xx;
            if (!solid[j] || visited[j]) break outer;
          }
          y1++;
        }

        for (let yy = y; yy <= y1; yy++) {
          for (let xx = x; xx <= x1; xx++) visited[yy * d.W + xx] = 1;
        }

        const wx = ((x + x1 + 1) / 2 - d.W / 2) * ts;
        const wz = ((y + y1 + 1) / 2 - d.H / 2) * ts;
        const halfX = ((x1 - x + 1) * ts) / 2;
        const halfZ = ((y1 - y + 1) * ts) / 2;

        this.entities.push(createWall(this._ctx, wx, wz, halfX, halfZ, 'dungeon-wall'));
        this.blockers.push({ x: wx, z: wz, halfX, halfZ });
      }
    }

    this._buildPropColliders(d);
  }

  /**
   * Turns collidable props (columns, chests, sarcophagi, braziers) into static
   * blockers, driven entirely by each catalog row's `collider` field. A prop the
   * connectivity guard demoted (`noCollider`) is skipped. Half-extents are read
   * in world units and clamped so a prop never oversteps its own tile.
   * @param {object} d
   * @private
   */
  _buildPropColliders(d) {
    const ts = this._tileSize;
    const half = ts / 2;
    for (const prop of d.props) {
      if (prop.noCollider) continue;
      const collider = DUNGEON_PROPS[prop.kind]?.collider;
      if (!collider) continue;

      const { x, z } = this.tileToWorld(prop.x, prop.y);
      // Rotated props (quarter/half turns) swap their X/Z extents.
      const quarter = Math.round((prop.rot ?? 0) / (Math.PI / 2)) % 2 !== 0;
      let halfX = quarter ? collider.halfZ : collider.halfX;
      let halfZ = quarter ? collider.halfX : collider.halfZ;
      halfX = Math.min(halfX, half);
      halfZ = Math.min(halfZ, half);

      this.entities.push(
        createProp(this._ctx, x, z, halfX, halfZ, collider.blocksProjectiles, `prop-${prop.kind}`)
      );
      this.blockers.push({ x, z, halfX, halfZ });
    }
  }

  /**
   * Treasure-room chests are drawn by the instanced dungeon builder and blocked
   * by `_buildPropColliders`; this adds the separate `E` target that opens them.
   * @param {object} d
   * @private
   */
  _buildChestInteractions(d) {
    let chestIndex = 0;
    for (const prop of d.props) {
      if (prop.kind !== 'chest') continue;
      const { x, z } = this.tileToWorld(prop.x, prop.y);
      const entity = this._ctx.world.createEntity('chest');
      entity.add(new Transform(x, DROPS.hoverHeight, z));
      // Index matches this same walk of `d.props`, which the builder replays
      // identically (see `case 'chest':` in DungeonBuilder#build) — so the Nth
      // chest interaction here is always the Nth chest instance visually.
      entity.add(new Chest({ index: chestIndex }));
      entity.add(new InteractionTarget('Chest'));
      this.entities.push(entity);
      chestIndex++;
    }
  }

  /**
   * Shrine rooms get one guaranteed weapon on their altar: a real pickup
   * entity floating over the pedestal, using the same trigger + `E`-to-swap
   * flow as any other weapon drop (see {@link ../../entities/PickupEntity.js}).
   * The altar itself (`shrineCrystal` prop) stays a static collider from
   * {@link _buildPropColliders} — this only adds the grabbable prize on top.
   *
   * Unlike a battlefield drop, this one is a fixed room fixture: it strips its
   * `Lifetime` so it never despawns while the player explores the rest of the
   * floor.
   * @param {object} d
   * @private
   */
  _buildShrineWeapons(d) {
    for (const prop of d.props) {
      if (prop.kind !== 'shrineCrystal') continue;
      const { x, z } = this.tileToWorld(prop.x, prop.y);
      const entity = createPickup(this._ctx, x, z, rollWeaponPowerup());
      entity.remove(Lifetime);
      this.entities.push(entity);
    }
  }

  /**
   * Instantiates the generator's pre-placed enemies at build time (issue #44).
   * They start in their archetype's idle state and only aggro once its vision
   * cone (or a plain seek-range check, for archetypes without one) picks up the
   * player, so the dungeon reads as explorable rather than a wall of aggro on
   * entry. Positions already exclude the entrance safe radius, the exit pad,
   * doorways, pools and solid props (decided in the pure generator).
   *
   * `facing` must reach {@link ../../entities/Enemy.js createEnemy} as an option
   * rather than being set on the `Transform` afterward: idle-static states
   * anchor their cone to whatever facing the brain/AI component captured at
   * construction, so applying it post-hoc left them always facing world 0.
   * @param {object} d
   * @private
   */
  _populateEnemies(d) {
    this._prePlacedCount = 0;
    const spawns = d.spawns ?? [];
    for (const spawn of spawns) {
      if (!spawn.archetype) continue;
      const { x, z } = this.tileToWorld(spawn.x, spawn.y);
      const facing = typeof spawn.facing === 'number' ? spawn.facing : 0;
      const enemy = createEnemy(this._ctx, x, z, spawn.archetype, { idleLife: true, facing });
      // Tag so the wave director never waits on an undiscovered pre-placed enemy.
      enemy.prePlaced = true;
      enemy.fromWave = false;
      this._prePlacedCount++;
    }
  }

  /** @param {object} d @private */
  _collectFloorSpawns(d) {
    this._floorSpawns = [];
    for (let y = 0; y < d.H; y++) {
      for (let x = 0; x < d.W; x++) {
        const c = y * d.W + x;
        if (d.grid[c] !== FLOOR) continue;
        if (d.doorway[c] || d.lakeMask[c]) continue;
        if (d.solidProp && d.solidProp[c]) continue;
        const rid = d.roomId[c];
        if (rid < 0) continue;
        const room = d.rooms[rid];
        if (room.type === TYPE.ENTRANCE) continue;
        const w = this.tileToWorld(x, y);
        this._floorSpawns.push({ x: w.x, z: w.z, roomId: rid });
      }
    }
    // Prefer combat/elite/boss rooms for enemy spawns
    this._floorSpawns.sort((a, b) => {
      const ta = d.rooms[a.roomId].type;
      const tb = d.rooms[b.roomId].type;
      const score = (t) =>
        t === TYPE.COMBAT || t === TYPE.ELITE || t === TYPE.BOSS ? 0 : t === TYPE.TREASURE ? 1 : 2;
      return score(ta) - score(tb);
    });
  }

  /**
   * Places destructible props (crates, explosive barrels) as *individual*
   * entities — each with its own collider, health and instanced visual — so a
   * single one can be broken without touching the merged static blockers. Only
   * combat, elite and treasure rooms get them; barrels are confined to combat
   * and elite rooms per the design.
   * @param {object} d Generated dungeon.
   * @private
   */
  _buildDestructibles(d) {
    const rng = makeRng((d.seed ^ 0x5bd1e995) >>> 0);
    const W = d.W;
    const idx = (x, y) => y * W + x;

    // A prop may never share a cell with an existing decorative prop, a spawn
    // pad, a doorway or a non-floor tile.
    const occ = new Uint8Array(d.W * d.H);
    for (let i = 0; i < occ.length; i++) {
      if (d.grid[i] !== FLOOR || d.doorway[i] || d.lakeMask[i]) occ[i] = 1;
    }
    for (const p of d.props) if (this._inBounds(p.x, p.y)) occ[idx(p.x, p.y)] = 1;
    for (const s of d.spawns) if (this._inBounds(s.x, s.y)) occ[idx(s.x, s.y)] = 1;

    /** @type {{ kind: string, tx: number, ty: number, rot: number }[]} */
    const placements = [];
    for (const room of d.rooms) {
      const plan = this._destructiblePlan(room, rng);
      if (!plan) continue;

      const cells = this._freeRoomCells(d, room, occ);
      for (let i = cells.length - 1; i > 0; i--) {
        const j = rng.i(0, i);
        const t = cells[i];
        cells[i] = cells[j];
        cells[j] = t;
      }

      let ci = 0;
      for (const [kind, count] of plan) {
        for (let n = 0; n < count && ci < cells.length; n++, ci++) {
          const cell = cells[ci];
          occ[idx(cell.x, cell.y)] = 1;
          placements.push({ kind, tx: cell.x, ty: cell.y, rot: rng.f(0, Math.PI * 2) });
        }
      }
    }

    if (placements.length === 0) return;

    /** @type {{ kind: string, x: number, z: number, rot: number, scale: number, leavesRubble: boolean, entity: import('../../ecs/Entity.js').Entity }[]} */
    const visual = [];
    for (const pl of placements) {
      const def = DUNGEON_PROPS[pl.kind];
      if (!def) continue;
      const w = this.tileToWorld(pl.tx, pl.ty);
      const entity = this._createDestructible(w.x, w.z, pl.rot, def);
      visual.push({
        kind: def.kind,
        x: w.x,
        z: w.z,
        rot: pl.rot,
        scale: def.visualScale,
        leavesRubble: Boolean(def.destructible?.leavesRubble),
        entity
      });
    }

    const handles = this._builder.mountDestructibles(visual);
    for (let i = 0; i < visual.length; i++) {
      const destructible = visual[i].entity.get(Destructible);
      if (destructible) destructible.handle = handles[i] ?? null;
    }
  }

  /**
   * How many of each prop a room gets, or null for rooms that get none.
   * @param {object} room Room record.
   * @param {ReturnType<typeof makeRng>} rng Seeded RNG.
   * @returns {[string, number][] | null}
   * @private
   */
  _destructiblePlan(room, rng) {
    switch (room.type) {
      case TYPE.COMBAT:
        return [['crate', rng.i(1, 3)], ['barrel', rng.i(1, 2)]];
      case TYPE.ELITE:
        return [['crate', rng.i(2, 4)], ['barrel', rng.i(2, 3)]];
      case TYPE.TREASURE:
        return [['crate', rng.i(1, 2)]];
      default:
        return null;
    }
  }

  /**
   * Free interior floor cells of a room, excluding anything already occupied.
   * Placing only inside a room's interior — never on a doorway — means a broken
   * (or unbroken) prop can never sever the level graph.
   * @param {object} d Generated dungeon.
   * @param {object} room Room record.
   * @param {Uint8Array} occ Occupancy grid.
   * @returns {{ x: number, y: number }[]}
   * @private
   */
  _freeRoomCells(d, room, occ) {
    const W = d.W;
    const idx = (x, y) => y * W + x;
    const cells = [];
    const x0 = Math.max(1, Math.floor(room.cx - room.w / 2) + 1);
    const x1 = Math.min(d.W - 2, Math.ceil(room.cx + room.w / 2) - 1);
    const y0 = Math.max(1, Math.floor(room.cy - room.h / 2) + 1);
    const y1 = Math.min(d.H - 2, Math.ceil(room.cy + room.h / 2) - 1);
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        const c = idx(x, y);
        if (occ[c] || d.grid[c] !== FLOOR || d.roomId[c] !== room.id) continue;
        cells.push({ x, y });
      }
    }
    return cells;
  }

  /**
   * Builds one destructible (or indestructible) prop entity: transform, static
   * collider, and — when the row calls for it — health, the destructible marker
   * and the explosive blast. Also registers its blocker so spawns avoid it.
   * @param {number} x World X.
   * @param {number} z World Z.
   * @param {number} rot Y rotation (visual only).
   * @param {import('../../config/DungeonProps.js').DungeonPropDef} def Prop row.
   * @returns {import('../../ecs/Entity.js').Entity}
   * @private
   */
  _createDestructible(x, z, rot, def) {
    const entity = this._ctx.world.createEntity(`destructible-${def.kind}`);
    entity.add(new Transform(x, 0, z));
    entity.add(
      new Collider({
        halfX: def.collider.halfX,
        halfZ: def.collider.halfZ,
        layer: Layer.WALL,
        mask: Layer.PLAYER | Layer.ENEMY | Layer.PLAYER_BULLET | Layer.ENEMY_BULLET,
        isStatic: true
      })
    );

    const blocker = { x, z, halfX: def.collider.halfX, halfZ: def.collider.halfZ };
    this.blockers.push(blocker);
    this.entities.push(entity);

    if (def.destructible) {
      const spec = def.destructible;
      const destructible = new Destructible({
        type: def.kind,
        destroyedBy: spec.destroyedBy,
        debrisCount: spec.debrisCount,
        leavesRubble: spec.leavesRubble,
        loot: spec.loot
      });
      destructible.blocker = blocker;
      entity.add(new Health(spec.maxHealth));
      entity.add(destructible);
    }

    if (def.explosive) {
      entity.add(
        new Explosive({ radius: def.explosive.radius, damage: def.explosive.damage, mask: 0 })
      );
      entity.add(
        new ExplosiveTrigger({ affectsEnvironment: def.explosive.affectsEnvironment })
      );
    }

    return entity;
  }

  /**
   * A destructible died: hide its instance, splice its blocker (never add one),
   * drop it from the arena entity list and spill any loot. Called for every
   * death; a no-op for entities that carry no {@link Destructible}.
   * @param {import('../../ecs/Entity.js').Entity} entity Entity that died.
   * @private
   */
  _onEntityDied(entity) {
    const destructible = entity.get(Destructible);
    if (!destructible) return;

    // Destruction only ever *removes* a blocker, so connectivity can only improve
    // — findSpawnPoint and anything else reading `_blockers` stay correct.
    if (destructible.blocker) {
      const bi = this.blockers.indexOf(destructible.blocker);
      if (bi !== -1) this.blockers.splice(bi, 1);
    }

    const ei = this.entities.indexOf(entity);
    if (ei !== -1) this.entities.splice(ei, 1);

    this._builder?.destroyDestructible(destructible.handle);

    if (destructible.loot) this._spawnLoot(entity, destructible.loot);
  }

  /**
   * @param {import('../../ecs/Entity.js').Entity} entity Dead prop.
   * @param {string} key Powerup key.
   * @private
   */
  _spawnLoot(entity, key) {
    const transform = entity.get(Transform);
    if (!transform) return;
    try {
      createPickup(this._ctx, transform.position.x, transform.position.z, key);
    } catch (err) {
      console.warn('[DungeonArena] destructible loot skipped:', key, err);
    }
  }

  /**
   * @param {number} x Tile X.
   * @param {number} y Tile Y.
   * @returns {boolean} Whether the tile is inside the grid.
   * @private
   */
  _inBounds(x, y) {
    return x >= 0 && y >= 0 && x < this._W && y < this._H;
  }

  /**
   * @param {THREE.Vector3} from
   * @param {number} minDistance
   * @param {number} radius
   * @param {{ x: number, z: number }} out
   * @returns {boolean}
   */
  findSpawnPoint(from, minDistance, radius, out) {
    if (this._floorSpawns.length === 0) return false;
    const minSq = minDistance * minDistance;
    const n = this._floorSpawns.length;
    const start = (Math.random() * n) | 0;

    for (let k = 0; k < Math.min(n, 80); k++) {
      const p = this._floorSpawns[(start + k * 17) % n];
      const dx = p.x - from.x;
      const dz = p.z - from.z;
      if (dx * dx + dz * dz < minSq) continue;
      if (this.blockers.some((b) => this._overlaps(b, p.x, p.z, radius, radius, 0.4))) continue;
      // Keep away from exit
      const ex = p.x - this.exit.x;
      const ez = p.z - this.exit.z;
      if (ex * ex + ez * ez < (this.exitRadius + 2) ** 2) continue;
      out.x = p.x;
      out.z = p.z;
      return true;
    }
    return false;
  }

  /**
   * @param {{x:number,z:number,halfX:number,halfZ:number}} a
   * @private
   */
  _overlaps(a, x, z, halfX, halfZ, padding) {
    return (
      Math.abs(a.x - x) < a.halfX + halfX + padding &&
      Math.abs(a.z - z) < a.halfZ + halfZ + padding
    );
  }

  /**
   * Whether the player is standing on the exit (B).
   * @param {number} x
   * @param {number} z
   * @returns {boolean}
   */
  isAtExit(x, z) {
    const dx = x - this.exit.x;
    const dz = z - this.exit.z;
    return dx * dx + dz * dz <= this.exitRadius * this.exitRadius;
  }

  /**
   * The arena's navigation grid, built once from the merged wall/pool blockers
   * the same way collision reads them. Cached: every AI system that asks gets
   * the identical instance, so the dungeon is rasterised exactly once per level.
   * @returns {NavGrid | null} Null before the level has bounds.
   */
  getNavGrid() {
    if (this._navGrid) return this._navGrid;
    if (!this.bounds) return null;
    this._navGrid = new NavGrid({
      bounds: this.bounds,
      blockers: this.blockers,
      cellSize: NAV_CELL_SIZE,
      solidHeight: this._ctx.config.dungeon?.wallHeight ?? 0
    });
    return this._navGrid;
  }

  /** @param {number} dt */
  update(dt) {
    this._builder?.update(dt);
  }

  dispose() {
    this._diedUnsub?.();
    this._diedUnsub = null;
    this._chestOpenedUnsub?.();
    this._chestOpenedUnsub = null;
    for (const entity of this.entities) this._ctx.world.destroyEntity(entity);
    this.entities.length = 0;
    this.blockers.length = 0;
    this._floorSpawns.length = 0;
    this._navGrid = null;
    this._builder?.dispose();
    this._builder = null;
    this.group = null;
    this.dungeon = null;
  }
}
